import * as Sentry from '@sentry/react';

/**
 * User-facing strings for Firebase Auth error codes used on Engage login.
 * These are expected user or browser conditions, not product defects.
 */
const USER_FACING_AUTH_MESSAGES = {
  'auth/email-already-in-use':
    'An account already exists with this email. Try signing in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Choose a stronger password (at least 6 characters).',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'Email or password is incorrect.',
  'auth/wrong-password': 'Email or password is incorrect.',
  'auth/invalid-credential': 'Email or password is incorrect.',
  'auth/too-many-requests': 'Too many attempts. Please wait and try again.',
  'auth/network-request-failed':
    'Network error. Check your connection and try again.',
  'auth/popup-closed-by-user': 'Popup closed by user.',
  'auth/popup-blocked':
    'Popup blocked by browser. Please allow popups and try again.',
  'auth/cancelled-popup-request':
    'Popup request cancelled. Please try signing in again.',
  'auth/missing-email': 'Please enter your email address.',
  'app/missing-first-name': 'Please enter your first name.',
  'app/missing-last-name': 'Please enter your last name.',
  'app/no-signed-in-user-for-verify':
    'You are signed out. Sign in again, then request a verification email.',
  'app/verification-email-claim-conflict':
    'This address is already tied to another login for this app (for example email and password). ' +
    'Sign out, then sign in with that method to verify — or ask an administrator to link your Google account to that profile.',
  'app/verification-email-send-failed': 'Email could not be sent',
};

/**
 * True when Engage already shows a specific message for this auth failure.
 */
export function isUserFacingAuthError(error) {
  const code = error?.code;
  return (
    typeof code === 'string' &&
    Object.prototype.hasOwnProperty.call(USER_FACING_AUTH_MESSAGES, code)
  );
}

/**
 * Report an auth failure that has no user-facing mapping.
 * Expected codes such as auth/popup-blocked stay out of Sentry.
 */
export function captureUnmappedAuthError(error) {
  if (!error || isUserFacingAuthError(error)) {
    return;
  }
  Sentry.captureException(error);
}

export default function mapFirebaseAuthError(error) {
  if (isUserFacingAuthError(error)) {
    return USER_FACING_AUTH_MESSAGES[error.code];
  }
  if (error?.message) {
    console.error('Firebase Auth error (unmapped):', error.message, error);
  }
  captureUnmappedAuthError(error);
  return 'Something went wrong. Please try again in a moment.';
}
